import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import { Loader2 } from "lucide-react";
import BreadCrumb from "Common/BreadCrumb";
import { apiFetch } from "../../api/api";

type SurveySummaryItem = {
    FID: number;
    QID: number;
    QTitle: string;
    OTitle: string;
    Values: number;
};

type ChartData = {
    title: string;
    labels: string[];
    series: number[];
};

const SurveyCharts = () => {
    const { form_id } = useParams<{ form_id: string }>();
    const navigate = useNavigate();

    const [results, setResults] = useState<SurveySummaryItem[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchResults = async () => {
            setLoading(true);

            try {
                const res = await apiFetch({
                    data: { params: [Number(form_id), 0, 0, 0, 5, 5, null] },
                });

                if (!res?.success || !Array.isArray(res.rows) || !res.rows.length) {
                    setResults([]);
                    return;
                }

                const rawRes = res.rows[0]?.Res;

                if (!rawRes) {
                    setResults([]);
                    return;
                }

                const parsed = JSON.parse(rawRes);

                if (Array.isArray(parsed)) {
                    setResults(parsed);
                } else {
                    setResults([]);
                }
            } catch (error) {
                console.error("خطا در دریافت یا پردازش نتایج نظرسنجی:", error);
                setResults([]);
            } finally {
                setLoading(false);
            }
        };

        fetchResults();
    }, [form_id]);

    const charts: ChartData[] = useMemo(() => {
        const grouped = new Map<number, ChartData>();

        for (const item of results) {
            if (!grouped.has(item.QID)) {
                grouped.set(item.QID, {
                    title: item.QTitle,
                    labels: [],
                    series: [],
                });
            }

            const chart = grouped.get(item.QID)!;
            chart.labels.push(item.OTitle);
            chart.series.push(Number(item.Values) || 0);
        }

        return Array.from(grouped.values());
    }, [results]);

    return (
        <>
            <BreadCrumb title="نمودار نتایج نظرسنجی" pageTitle="Survey Charts" />

            <button
                onClick={() => navigate(`/forms/${form_id}`)}
                className="flex items-center gap-2 bg-custom-500 text-white px-5 py-2 rounded-xl hover:bg-custom-600 transition-all shadow-sm"
            >
                بازگشت
            </button>

            <div className="w-11/12 mx-auto mt-8">
                {loading ? (
                    <div className="flex justify-center py-10 text-slate-500">
                        <Loader2 className="animate-spin mr-2" />
                        در حال بارگذاری...
                    </div>
                ) : charts.length === 0 ? (
                    <div className="flex justify-center py-10 text-slate-500">
                        داده‌ای برای نمایش وجود ندارد
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 gap-8">
                        {charts.map((chart, index) => (
                            <div
                                key={`${chart.title}-${index}`}
                                className="bg-white dark:bg-zink-800 p-6 rounded-lg shadow"
                            >
                                <h3 className="text-sm font-bold mb-4 text-center">
                                    {chart.title}
                                </h3>

                                <ReactApexChart
                                    type="pie"
                                    height={350}
                                    series={chart.series}
                                    options={{
                                        chart: {
                                            toolbar: {
                                                show: false,
                                            },
                                        },
                                        labels: chart.labels,
                                        legend: {
                                            position: "bottom",
                                        },
                                        dataLabels: {
                                            formatter: (val: number) => `${val.toFixed(1)}%`,
                                        },
                                        tooltip: {
                                            y: {
                                                formatter: (value: number) => `${value} رأی`,
                                            },
                                        },
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
};

export default SurveyCharts;
